import type {ReactNode} from "react";
import {OtsBadge} from "../components/OtsBadge";
import {formatAgo, formatHoursToFull, type SiteContainerRow, type SiteContainers} from "./site-containers";
import {SiteContainerFillBar} from "./SiteContainerFillBar";

export type SiteContainerSummaryProps = {
  summary: SiteContainers;
  now: number;
  /** The container's name, e.g. as a link to its profile; plain text when left out. */
  renderName?: (row: SiteContainerRow) => ReactNode;
  className?: string;
};

const heading = "text-[11px] font-semibold uppercase tracking-wide text-[var(--ots-text-muted)]";
const volume = (value: number | undefined) => value == null ? "—" : Math.round(value).toLocaleString();

type StatProps = {label: string; value: ReactNode; tone?: "success" | "danger"; title?: string; small?: boolean};

const Stat = ({label, value, tone, title, small}: StatProps) => (
  <div className="flex min-w-0 flex-col gap-0.5" title={title}>
    <span className={heading}>{label}</span>
    <span className={[
      small ? "line-clamp-2 text-sm leading-tight" : "truncate text-base tabular-nums",
      "font-semibold",
      tone === "success" ? "text-[var(--ots-success)]" : tone === "danger" ? "text-[var(--ots-error)]" : "text-[var(--ots-text)]",
    ].join(" ")}>{value}</span>
  </div>
);

type ContainerRowProps = {
  row: SiteContainerRow;
  now: number;
  renderName?: SiteContainerSummaryProps["renderName"];
  /** Only worth repeating on each row when the site's containers have different haulers. */
  showHauler: boolean;
};

const ContainerRow = ({row, now, renderName, showHauler}: ContainerRowProps) => {
  const fills = formatHoursToFull(row.hoursToFull);
  return <li className="flex flex-col gap-1.5">
    <div className="flex items-baseline justify-between gap-2 text-xs">
      <span className="min-w-0 truncate font-semibold text-[var(--ots-text)]">{renderName ? renderName(row) : row.name}</span>
      <span className={[
        "shrink-0 font-semibold tabular-nums",
        row.availableLoads > 0 ? "text-[var(--ots-success)]" : "text-[var(--ots-text-muted)]",
      ].join(" ")}>
        {row.availableLoads} {row.availableLoads === 1 ? "load" : "loads"}
      </span>
    </div>
    <SiteContainerFillBar
      label={`${row.name} level`}
      percent={row.percent}
      oilPercent={row.oilPercent}
      readyPercent={row.readyPercent}
      tone={row.tone}/>
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-[var(--ots-text-muted)]">
      <span className="tabular-nums">
        {row.percent == null ? "No level" : `${Math.round(row.percent)}%`} · {volume(row.volume)} / {volume(row.capacity)} bbl
      </span>
      {fills && (
        <span className={row.hoursToFullCritical ? "font-semibold text-[var(--ots-error)]" : undefined}>Full in {fills}</span>
      )}
      {row.stalePoll && (
        <OtsBadge size="compact" tone="warning">{row.polledAt ? `Polled ${formatAgo(row.polledAt, now)}` : "Never polled"}</OtsBadge>
      )}
      {row.doNotUse && <OtsBadge size="compact" tone="danger">Do Not Use</OtsBadge>}
      {!row.doNotUse && row.doNotDispatch && <OtsBadge size="compact" tone="warning">Do Not Dispatch</OtsBadge>}
      {showHauler && row.hauler && <span className="ml-auto truncate">{row.hauler}</span>}
    </div>
  </li>;
};

/** A site's containers for the map's site panel: loads ready, how soon it fills, who hauls, and each container's level. */
export const SiteContainerSummary = ({summary, now, renderName, className}: SiteContainerSummaryProps) => {
  const fills = formatHoursToFull(summary.hoursToFull);
  const [hauler, ...otherHaulers] = summary.haulers;
  // Not a landmark of its own: the panel's Containers section already names it.
  return <div className={["flex flex-col gap-4 text-[var(--ots-text)]", className].filter(Boolean).join(" ")}>
    {/* The hauler takes what the two numbers leave; names run long. */}
    <div className="grid grid-cols-[auto_auto_minmax(0,1fr)] gap-5">
      <Stat label="Avail. Loads" value={summary.availableLoads} tone={summary.availableLoads > 0 ? "success" : undefined}/>
      <Stat label="Fills in" value={fills ?? "—"} tone={summary.hoursToFullCritical ? "danger" : undefined}/>
      <Stat
        label="Hauler"
        value={hauler ? <>{hauler}{otherHaulers.length > 0 && <span className="text-xs text-[var(--ots-text-muted)]"> +{otherHaulers.length}</span>}</> : "Unassigned"}
        title={summary.haulers.join(", ") || undefined}
        small/>
    </div>
    {summary.containers.length > 0
      ? <ul className="flex flex-col gap-3">
        {summary.containers.map(row => (
          <ContainerRow key={row.id} row={row} now={now} renderName={renderName} showHauler={summary.haulers.length > 1}/>
        ))}
      </ul>
      : <p className="text-xs text-[var(--ots-text-muted)]">No containers at this site.</p>}
  </div>;
};
